import { CreateLogger } from "@swivl-mono/helpers";
import { type CCVoiceToken, Log } from "@swivl-mono/types";
import { Call, Device } from "@twilio/voice-sdk";

import { CCAudioDevices } from "./CCAudioDevices";
import ContactCenterV3Model from "./ContactCenterV3.Store";

const log = CreateLogger(Log.VOICE, "CCVoiceDevice");

/** Peak below this counts as silence for the dead-microphone watch. */
const DEAD_MIC_THRESHOLD = 0.01;
/** How long the mic must stay silent mid-call before the agent is warned. */
const DEAD_MIC_SILENCE_MS = 8000;

export type CCPendingActionType = "listen" | "barge" | "bridge";
export interface CCPendingAction {
  type: CCPendingActionType;
  callSid: string;
}

export type CCIncomingDecision = { action: "reject"; reason: string } | { action: "accept"; pending: CCPendingAction | null };

/**
 * What to do with an incoming leg (pure, exported for its test). One call per
 * tab (ENG-3656): with a Device per Twilio account a second leg can arrive on
 * another Device, and before this any incoming was auto-accepted and quietly
 * replaced the active call. A pending action (listen / barge / bridge) or an
 * unexpected leg (outbound call joining) is accepted only when nothing is live.
 */
export function decideIncomingLeg(hasActiveCall: boolean, pending: CCPendingAction | null): CCIncomingDecision {
  if (hasActiveCall) return { action: "reject", reason: "a call is already active on this tab" };
  return { action: "accept", pending };
}

/**
 * Standalone Twilio Device manager for Contact Center V3.
 * Completely independent from the old system's CallModel.
 *
 * Initialized when the agent enters the CC page. Stays alive
 * across CC page navigation. Destroyed on unmount.
 *
 * One Device per Twilio account (ENG-3656): an agent leg is dialed on the
 * account that owns the call's number, and a Device registered on another
 * account never hears it. An org with numbers on the platform account and on
 * its own subaccount (every org mid-migration) therefore needs a registration
 * on each. The server hands back one token per account, all with this tab's
 * identity, and each gets its own Device here. Whichever Device receives the
 * leg becomes the active one for the call's lifetime.
 */
export class CCVoiceDevice {
  /** Registered Devices by the Twilio account SID they are registered on. */
  private static devices = new Map<string, Device>();
  private static activeCall: Call | null = null;
  /** The Device holding `activeCall` — audio settings mid-call go here. */
  private static activeDevice: Device | null = null;
  private static pendingAction: CCPendingAction | null = null;
  /** The init in flight, so concurrent callers share it instead of each building the Devices. */
  private static initInFlight: Promise<void> | null = null;

  /**
   * Initialize the Twilio Devices with this tab's CC v3 tokens.
   * Call this when the agent enters any CC v3 page.
   *
   * Single-flight (ENG-3469): the presence host and the Conversations page both
   * call this on a cold load, milliseconds apart. The old guard only checked
   * for a finished Device, which is created AFTER the token await, so both
   * callers passed it and TWO Devices registered for the same identity on the
   * same account. The agent leg rang both; one accepted, Twilio cancelled the
   * other, and its cancel handler cleared the call bar for the call that was
   * still live.
   */
  static init(): Promise<void> {
    if (CCVoiceDevice.devices.size > 0) return Promise.resolve(); // Already initialized
    if (CCVoiceDevice.initInFlight) return CCVoiceDevice.initInFlight;
    CCVoiceDevice.initInFlight = CCVoiceDevice.initOnce().finally(() => {
      CCVoiceDevice.initInFlight = null;
    });
    return CCVoiceDevice.initInFlight;
  }

  private static async initOnce(): Promise<void> {
    const tokens = await ContactCenterV3Model.ensureVoiceTokens();
    if (tokens.length === 0) {
      log("No token available — skipping Device init");
      return;
    }

    log(`Initializing ${tokens.length} Twilio Device(s): ${tokens.map((t) => `${t.account}:${t.accountSid}`).join(", ")}`);
    // Each account registers independently: one failing (a key that is not
    // inside that account, a network blip) must not take the others down.
    const results = await Promise.allSettled(tokens.map((token) => CCVoiceDevice.registerDevice(token)));
    results.forEach((result, i) => {
      if (result.status === "rejected") log(`Device on ${tokens[i].account} account ${tokens[i].accountSid} failed to register`, result.reason);
    });
    log(`${CCVoiceDevice.devices.size} of ${tokens.length} Device(s) registered and ready`);
  }

  private static async registerDevice(voiceToken: CCVoiceToken): Promise<void> {
    const { accountSid, account } = voiceToken;
    const device = new Device(voiceToken.token);

    // Every incoming leg here is auto-accepted (conference joins — outbound,
    // listen, barge), so the SDK's incoming ringtone is a jarring split-second
    // chirp before accept, never an actual ring-to-answer. Silence it.
    device.audio?.incoming(false);

    device.on("incoming", (call: Call) => CCVoiceDevice.onIncoming(device, accountSid, call));

    device.on("tokenWillExpire", async () => {
      log(`Token will expire on account ${accountSid} — refreshing`);
      ContactCenterV3Model.setState({ twilioToken: null, twilioTokens: null }); // Force re-fetch
      const fresh = (await ContactCenterV3Model.ensureVoiceTokens()).find((t) => t.accountSid === accountSid);
      if (fresh) device.updateToken(fresh.token);
      else log(`No fresh token for account ${accountSid} — this Device will drop at expiry`);
    });

    device.on("registered", () => log(`Device registered on ${account} account ${accountSid}`));
    device.on("error", (error) => log(`Device error on account ${accountSid}`, error));

    // Registration is what lets an inbound voice escalation reach this browser.
    // It costs no microphone permission on its own — Twilio acquires media when
    // a call actually connects.
    try {
      await device.register();
    } catch (err) {
      device.destroy();
      throw err;
    }
    CCVoiceDevice.devices.set(accountSid, device);

    // The saved microphone is applied at CALL TIME, not here (Chuck,
    // 2026-08-23). `device.audio.setInputDevice(...)` makes the Voice SDK call
    // getUserMedia to acquire the device, so doing it at startup opened the
    // microphone — and lit the browser's recording indicator — merely for
    // opening the Contact Center. Measured: one getUserMedia
    // ({audio:{deviceId:{exact:"default"}}}) from the Voice SDK on every page
    // load, whether or not permission had ever been granted.
  }

  private static onIncoming(device: Device, accountSid: string, call: Call): void {
    log(`Incoming call received on account ${accountSid}`);
    const decision = decideIncomingLeg(CCVoiceDevice.activeCall !== null, CCVoiceDevice.pendingAction);
    if (decision.action === "reject") {
      log(`Incoming leg on account ${accountSid} rejected — ${decision.reason}`);
      call.reject();
      return;
    }

    CCVoiceDevice.pendingAction = null;
    if (decision.pending) {
      log(`Auto-accepting for pending ${decision.pending.type} on ${decision.pending.callSid}`);
    } else {
      // Unexpected incoming call — auto-accept (agent was added to a conference)
      log("No pending action — auto-accepting incoming call");
    }
    call.accept();
    CCVoiceDevice.activeCall = call;
    CCVoiceDevice.activeDevice = device;
    CCVoiceDevice.registerCallEvents(device, call);
    // The softphone has the call now — this is the moment the bar may say
    // "On Call" (the store only set "dialing" when it asked for the bridge).
    // Listen-in stays "listening" (muted observer).
    if (decision.pending?.type !== "listen") ContactCenterV3Model.setState({ voiceState: "active", isMuted: false });
  }

  /**
   * Destroy every Device. Call on CC page unmount.
   */
  static destroy(): void {
    CCVoiceDevice.stopDeadMicWatch();
    if (CCVoiceDevice.devices.size > 0) {
      log(`Destroying ${CCVoiceDevice.devices.size} Device(s)`);
      for (const device of CCVoiceDevice.devices.values()) device.destroy();
      CCVoiceDevice.devices.clear();
      CCVoiceDevice.activeCall = null;
      CCVoiceDevice.activeDevice = null;
      CCVoiceDevice.pendingAction = null;
    }
  }

  /** Whether at least one Device is initialized and registered */
  static get isReady(): boolean {
    return CCVoiceDevice.devices.size > 0;
  }

  /**
   * The Device audio settings should target: the one holding the active call,
   * else the first registered one. Saved devices are applied at call start on
   * the Device that received the leg, so this only matters mid-call.
   */
  static get current(): Device | null {
    return CCVoiceDevice.activeDevice ?? CCVoiceDevice.devices.values().next().value ?? null;
  }

  /**
   * Set a pending action — the next incoming call will be auto-accepted for this action.
   * Called by the store's listenIn/bargeIn/bridgeToAgent before the API call.
   */
  static setPendingAction(type: CCPendingActionType, callSid: string): void {
    CCVoiceDevice.pendingAction = { type, callSid };
  }

  /** Disarm a pending action whose API call failed, so the next incoming leg is not mistaken for it. */
  static clearPendingAction(): void {
    CCVoiceDevice.pendingAction = null;
  }

  /** Disconnect the active call */
  static hangup(): void {
    // Every path that ends a call must release the microphone, or the browser
    // keeps reporting the tab as recording after the call is over.
    CCVoiceDevice.stopDeadMicWatch();
    if (CCVoiceDevice.activeCall) {
      CCVoiceDevice.activeCall.disconnect();
      CCVoiceDevice.activeCall = null;
      CCVoiceDevice.activeDevice = null;
    }
    ContactCenterV3Model.endVoiceSession();
  }

  /** Toggle mute on the active call */
  static toggleMute(): void {
    if (!CCVoiceDevice.activeCall) return;
    const currentMuted = ContactCenterV3Model.state.isMuted;
    CCVoiceDevice.activeCall.mute(!currentMuted);
    ContactCenterV3Model.setState({ isMuted: !currentMuted });
  }

  /**
   * Watch the microphone for the duration of a call and say so if it goes
   * silent. Sustained silence while connected is the failure that cost an
   * evening: the agent believes they are talking, the caller hears nothing, and
   * no other signal in the stack disagrees.
   */
  private static startDeadMicWatch(): void {
    CCVoiceDevice.stopDeadMicWatch();
    let silentSince: number | null = null;
    void CCAudioDevices.startMeter(ContactCenterV3Model.state.audioInputDeviceId ?? undefined, (peak) => {
      ContactCenterV3Model.setState({ audioInputLevel: peak });
      if (peak >= DEAD_MIC_THRESHOLD) {
        silentSince = null;
        if (ContactCenterV3Model.state.audioInputDead) ContactCenterV3Model.setState({ audioInputDead: false });
        return;
      }
      // Nobody talks constantly, so only sustained silence counts.
      if (silentSince === null) silentSince = Date.now();
      else if (Date.now() - silentSince > DEAD_MIC_SILENCE_MS && !ContactCenterV3Model.state.audioInputDead) {
        log("microphone has been silent for the whole call — warning the agent");
        ContactCenterV3Model.setState({ audioInputDead: true });
      }
    });
  }

  private static stopDeadMicWatch(): void {
    if (!CCAudioDevices.meterRunning) return;
    void CCAudioDevices.stopMeter();
    ContactCenterV3Model.setState({ audioInputLevel: 0, audioInputDead: false });
  }

  private static registerCallEvents(device: Device, call: Call): void {
    // Now the microphone is genuinely needed, so applying the agent's saved
    // input device is honest here. Every call path — incoming, listen, barge,
    // outbound — funnels through this method, so this is the one place it has to
    // live. Best-effort: a device that can't be applied must not stop the call,
    // which will simply use the browser default.
    void CCAudioDevices.applySaved(device)
      .then((selectedInput) => ContactCenterV3Model.setState({ audioInputDeviceId: selectedInput ?? null }))
      .catch((err) => log("failed to apply saved audio device at call start", err));

    CCVoiceDevice.startDeadMicWatch();

    // Backstop (ENG-3469): only the call the softphone is actually holding may
    // end the session. A stray or superseded call's disconnect/cancel must not
    // clear the bar for a live call.
    const endIfCurrent = (what: string): void => {
      if (CCVoiceDevice.activeCall !== call) {
        log(`${what} on a call that is not the active one — ignored`);
        return;
      }
      CCVoiceDevice.stopDeadMicWatch();
      CCVoiceDevice.activeCall = null;
      CCVoiceDevice.activeDevice = null;
      ContactCenterV3Model.endVoiceSession();
    };

    call.on("disconnect", () => {
      log("Call disconnected");
      endIfCurrent("disconnect");
    });

    call.on("error", (error) => {
      log("Call error", error);
      endIfCurrent("error");
    });

    call.on("cancel", () => {
      log("Call cancelled");
      endIfCurrent("cancel");
    });
  }
}
